import React from 'react';
import Modal from '../Modal';
import { Exportateur } from '../../types/weighing';
import { GlobeAltIcon, MapPinIcon, PhoneIcon, EnvelopeIcon, TagIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface ExporterDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  exporter: Exportateur;
}

const ExporterDetailsModal: React.FC<ExporterDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  exporter 
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplay = (statut: string) => {
    return statut === 'actif' ? 'Actif' : 'Inactif';
  };

  const getStatusIcon = (statut: string) => {
    return statut === 'actif' ? (
      <CheckCircleIcon className="h-5 w-5 text-green-500" />
    ) : (
      <XCircleIcon className="h-5 w-5 text-red-500" />
    );
  };

  const getTypeDisplay = (type?: string) => {
    if (!type) return 'Non renseigné';
    const typeMap: { [key: string]: string } = {
      'entreprise-privee': 'Entreprise privée',
      'cooperative': 'Coopérative',
      'negociant': 'Négociant',
      'grossiste': 'Grossiste',
      'importateur': 'Importateur',
      'autre': 'Autre'
    };
    return typeMap[type] || type;
  };

  const getZoneDisplay = (zone?: string) => {
    if (!zone) return 'Non renseigné';
    const zoneMap: { [key: string]: string } = {
      'ouest-afrique': 'Afrique de l\'Ouest',
      'centre-afrique': 'Afrique Centrale',
      'nord-afrique': 'Afrique du Nord',
      'est-afrique': 'Afrique de l\'Est',
      'sud-afrique': 'Afrique du Sud',
      'europe': 'Europe',
      'asie': 'Asie',
      'amerique': 'Amérique',
      'autre': 'Autre'
    };
    return zoneMap[zone] || zone;
  };

  const getPaysDisplay = (pays?: string) => {
    if (!pays) return 'Non renseigné';
    const paysMap: { [key: string]: string } = {
      'senegal': 'Sénégal',
      'mali': 'Mali',
      'guinee': 'Guinée',
      'cote-ivoire': 'Côte d\'Ivoire',
      'ghana': 'Ghana',
      'nigeria': 'Nigeria',
      'cameroun': 'Cameroun',
      'tchad': 'Tchad',
      'niger': 'Niger',
      'burkina-faso': 'Burkina Faso',
      'autre': 'Autre'
    };
    return paysMap[pays] || pays;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails de l'exportateur" size="lg">
      <div className="space-y-6">
        {/* En-tête avec nom et statut */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <GlobeAltIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{exporter.nom}</h3>
              <div className="flex items-center space-x-2 mt-1">
                {getStatusIcon(exporter.statut)}
                <span className="text-sm text-gray-600">
                  {getStatusDisplay(exporter.statut)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Type et Pays */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Type d'exportateur
              </label>
              <div className="flex items-center space-x-2">
                <TagIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {getTypeDisplay(exporter.type)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Pays
              </label>
              <div className="flex items-center space-x-2">
                <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {getPaysDisplay(exporter.pays)}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">
                Zone géographique
              </label>
              <div className="flex items-center space-x-2">
                <GlobeAltIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  {getZoneDisplay(exporter.zone)}
                </span>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            {exporter.telephone && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Téléphone
                </label>
                <div className="flex items-center space-x-2">
                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{exporter.telephone}</span>
                </div>
              </div>
            )}

            {exporter.email && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{exporter.email}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Adresse */}
        {exporter.adresse && (
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">
              Adresse
            </label>
            <div className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg">
              <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
              <span className="text-sm text-gray-900">{exporter.adresse}</span>
            </div>
          </div>
        )}

        {/* Informations système */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-500 mb-3">Informations système</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">ID :</span>
              <span className="ml-2 text-gray-900 font-mono">{exporter.id}</span>
            </div>
            {exporter.created_at && (
              <div>
                <span className="text-gray-500">Créé le :</span>
                <span className="ml-2 text-gray-900">{formatDate(exporter.created_at)}</span>
              </div>
            )}
            {exporter.updated_at && (
              <div>
                <span className="text-gray-500">Modifié le :</span>
                <span className="ml-2 text-gray-900">{formatDate(exporter.updated_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ExporterDetailsModal;
